import React from 'react';
import { CheckCircle, XCircle, Wand2 } from 'lucide-react';
import { useAssessmentStore } from '../store';
import { determineAvatar } from '../utils/avatarLogic';
import AvatarResults from './AvatarResults';

interface AvatarGeneratorProps {
  onComplete: (avatarIndex: number) => void;
}

const AvatarGenerator: React.FC<AvatarGeneratorProps> = ({ onComplete }) => {
  const { 
    completedAssessments,
    dassResults,
    brsResults,
    cissResults
  } = useAssessmentStore();

  const [showResults, setShowResults] = React.useState(false);

  const totalAssessments = Object.keys(completedAssessments).length;
  const completedCount = Object.values(completedAssessments).filter(Boolean).length;
  const completionPercentage = (completedCount / totalAssessments) * 100;

  const canGenerateAvatar = completionPercentage === 100;

  const handleGenerateAvatar = () => {
    if (!dassResults || !brsResults || !cissResults) return;

    const avatarIndex = determineAvatar({
      dass: {
        depression: { severity: dassResults.depression.severity },
        anxiety: { severity: dassResults.anxiety.severity },
        stress: { severity: dassResults.stress.severity }
      },
      brs: {
        severity: brsResults.severity
      },
      ciss: {
        primaryStyle: cissResults.primaryStyle
      }
    });

    onComplete(avatarIndex);
    setShowResults(true);
  };

  if (showResults) {
    return <AvatarResults onBack={() => setShowResults(false)} avatarIndex={0} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">AI Avatar Generation</h2>

        {/* Progress Circle */}
        <div className="flex justify-center mb-8">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full" viewBox="0 0 36 36">
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#eee"
                strokeWidth="3"
              />
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="3"
                strokeDasharray={`${completionPercentage}, 100`}
              />
              <text x="18" y="20.35" className="text-lg font-medium" textAnchor="middle">
                {completionPercentage}%
              </text>
            </svg>
          </div>
        </div>

        {/* Assessment Status */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="font-medium">Emotional Assessment (DASS)</span>
            {completedAssessments.dass ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <XCircle className="w-6 h-6 text-red-500" />
            )}
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="font-medium">Resilience Scale (BRS)</span>
            {completedAssessments.brs ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <XCircle className="w-6 h-6 text-red-500" />
            )}
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="font-medium">Coping Style Scale (CISS)</span>
            {completedAssessments.ciss ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <XCircle className="w-6 h-6 text-red-500" />
            )}
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerateAvatar}
          disabled={!canGenerateAvatar}
          className={`
            w-full py-3 rounded-lg flex items-center justify-center
            ${canGenerateAvatar
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }
          `}
        >
          <Wand2 className="w-5 h-5 mr-2" />
          {canGenerateAvatar ? 'Generate AI Avatar' : 'Complete All Assessments'}
        </button>

        {!canGenerateAvatar && (
          <p className="mt-4 text-sm text-center text-gray-500">
            Complete all assessments to generate your personalized AI avatar
          </p>
        )}
      </div>
    </div>
  );
};

export default AvatarGenerator;